import { gql } from '@apollo/client/core';
import { ANNOUNCE_DETAIL_FIELDS } from '../Announce/fragments';

/**
 * The single event behind the event modals -- the announce modal, event edit, the
 * ticket-counts report and the Prism connector all render this one record.
 *
 * Field-for-field what the retired legacy `app { event(id:) }` document selected --
 * `sendWillCallReminders` included; it was the admin store's hand-written event type, not the
 * query, that omitted it. The announce block reuses `ANNOUNCE_DETAIL_FIELDS`, the same
 * selection the announce save returns, so the modal reads one shape whichever path filled it.
 *
 * `venueRoom.venue` selects `id` where the legacy document selected `name` alone.
 * `EventSummary.venueRoom` and `Event.venueRoom` are the same `VenueRoom` wire type, so this
 * selection and the list fragments' `venue { id name }` share one Apollo cache field key;
 * two different selections on it make Apollo warn and let one overwrite the other.
 */
export const EVENT_DETAIL_FIELDS = gql`
  fragment EventDetailFields on Event {
    id
    name
    state
    date
    doorTime
    startTime
    endTime
    availableForSalesTax
    availableForProcessingFee
    availableForDonation
    donationDescription
    donationButtonText
    mailPdfTickets
    sendWillCallReminders
    minimumAge
    confirmationEmailContent
    footerContent
    orderConfirmationContent
    fullSlug
    ticketLimit
    timezone
    connectedToPrism
    prismEventId
    venue {
      id
      name
      currency
    }
    venueRoom {
      id
      name
      venue {
        id
        name
      }
    }
    announceDetail {
      ...AnnounceDetailFields
    }
  }
  ${ANNOUNCE_DETAIL_FIELDS}
`;

/**
 * Fragment for fetching minimal event details. It avoids fetching associations.
 */
export const EVENT_SUMMARY_FRAGMENT = gql`
  fragment EventSummaryFields on Event {
    id
    name
    description
    startTime
    endTime
    doorTime
    slug
    timezone
  }
`;

/**
 * A row of the admin events list.
 */
export const EVENT_LIST_FIELDS = gql`
  fragment EventListFields on EventSummary {
    id
    name
    slug
    date
    startTime
    doorTime
    endTime
    timezone
    state
    ticketed
    holdPreference
    description
    footerContent
    minimumAge
    ticketsUrl
    ticketTotals {
      totalTicketsSalesToday
      totalTicketsSoldToday
      totalTicketsSales
      totalTicketsSold
      totalTicketsAvailable
      totalTickets
      totalTicketsOnHold
      totalSalesLimit
    }
    venue {
      id
      name
    }
    venueRoom {
      id
      name
      venue {
        id
        name
      }
    }
    eventGroup {
      id
      name
    }
    seatingChart {
      id
      name
    }
    announceDetail {
      announceAt
    }
  }
`;

/**
 * A row of the calendar and of the confirmed-dates lookup, which fetches up to
 * a thousand at a time.
 *
 * Deliberately omits `ticketTotals` — the backend resolves it per row through
 * ~15-25 unbatched aggregations — along with the `description` and
 * `footerContent` rich text, which no list consumer reads.
 */
export const EVENT_LIST_MINIMAL_FIELDS = gql`
  fragment EventListMinimalFields on EventSummary {
    id
    name
    slug
    date
    startTime
    doorTime
    endTime
    timezone
    state
    ticketed
    holdPreference
    venue {
      id
      name
    }
    venueRoom {
      id
      name
      venue {
        id
        name
      }
    }
    eventGroup {
      id
      name
    }
    seatingChart {
      id
      name
    }
    announceDetail {
      announceAt
    }
  }
`;

/**
 * The picker's read model. All four fields are load-bearing: the checkbox label carries the
 * name *and* the ticket's own sale window — that context is what makes an informed selection
 * possible, and it is what the legacy presale form showed — while the expanded read-only rows
 * render the names alone.
 */
export const EVENT_TICKET_TYPE_FIELDS = gql`
  fragment EventTicketTypeFields on EventTicketType {
    id
    name
    sellStart
    sellEnd
  }
`;
